// The explicit "war aborted" state (docs/ARCHITECTURE.md §13): the room connection was lost mid-war, the room
// refused to start the war, or the war crashed (with a selectable crash report). A themed DOM panel with the reason
// and a way back to setup; it draws nothing on the canvas.

import { InvariantViolation } from '@taxwar/net'
import { applyScreenTheme } from '../../hud/theme'
import type { Screen, ScreenFactory } from '../screen'

const OVERLAY_CLASS = 'war-aborted'
const PANEL_CLASS = 'war-aborted__panel tw-panel'
const REPORT_CLASS = 'war-aborted__report'
const BUTTON_CLASS = 'tw-button tw-button--primary'
const NEW_WAR_LABEL = 'NEW WAR'

export interface WarAbortedCopy {
  readonly title: string
  readonly detail: string
  /** Preformatted crash report (error summary, seed), or null when there is nothing to report. */
  readonly report: string | null
}

export function warAbortedScreen(copy: WarAbortedCopy, onNewWar: () => void): ScreenFactory<Screen> {
  return (context) => {
    const host = context.renderer.domElement.parentElement
    if (host === null) throw new InvariantViolation('the renderer canvas is not attached to a host element')
    const overlay = document.createElement('div')
    overlay.className = OVERLAY_CLASS
    const panel = document.createElement('section')
    panel.className = PANEL_CLASS
    panel.setAttribute('role', 'alertdialog')
    const title = document.createElement('h1')
    title.textContent = copy.title
    const detail = document.createElement('p')
    detail.textContent = copy.detail
    const button = document.createElement('button')
    button.type = 'button'
    button.className = BUTTON_CLASS
    button.textContent = NEW_WAR_LABEL
    button.addEventListener('click', onNewWar, { signal: context.signal })
    panel.append(title, detail)
    if (copy.report !== null) {
      const report = document.createElement('pre')
      report.className = REPORT_CLASS
      report.textContent = copy.report
      panel.appendChild(report)
    }
    panel.appendChild(button)
    overlay.appendChild(panel)
    applyScreenTheme(overlay, context.tuning, context.settings)
    host.appendChild(overlay)
    button.focus()
    return {
      resize: () => undefined,
      applySettings: (settings) => applyScreenTheme(overlay, context.tuning, settings),
      dispose: () => overlay.remove(),
    }
  }
}
